import {css, html, TemplateResult} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import {ifDefined} from 'lit/directives/if-defined.js';
import {input} from '../../../styles/inputs/input.styles';
import {SpBaseInput} from '../sp-base-input';
import {live} from 'lit/directives/live.js';
import {unsafeCSSIcon} from "../../../styles/icons";

@customElement('sp-input')
export class SpInput extends SpBaseInput<string | number> {
    static styles = [
        input,
        //language=CSS
        css`
            .number-wrapper {
                position: relative;
                width: 100%;
            }
            
            .number-wrapper .number-minus,
            .number-wrapper .number-plus {
                position: absolute;
                top: 2px;
                display: block;
                width: 41px;
                height: 41px;
                border-radius: 3px;
                background-repeat: no-repeat;
                background-position: center center;
                background-size: 100% auto;
                cursor: pointer;
            }
            
            .number-wrapper .number-minus {
                right: 45px;
                background-image: url(${ unsafeCSSIcon('minus-tiny', { size: 'x128' }) });
            }
            
            .number-wrapper .number-plus {
                right: 2px;
                background-image: url(${ unsafeCSSIcon('plus-tiny', { size: 'x128' }) });
            }`
    ];

    @property({ reflect: true }) type:
        | 'date'
        | 'datetime-local'
        | 'email'
        | 'number'
        | 'password'
        | 'search'
        | 'tel'
        | 'text'
        | 'time'
        | 'url' = 'text';
    @property() value;
    @property({type: Boolean, reflect: true}) disabled = false;
    @property({type: Boolean, reflect: true}) readonly = false;
    @property({ type: Boolean, reflect: true }) required = false;
    @property({type: Number}) minlength: number;
    @property({type: Number}) maxlength: number;
    @property({type: String}) placeholder: string;
    @property() min: number | string;
    @property() max: number | string;
    @property() step: number | string;

    protected render() {
        return this.conditionallyWrap(
            this.type === 'number',
            html`
                <input
                    part="input"
                    type=${this.type}
                    .value=${live(!!this.value ? this.value : '')}
                    @input=${event => {
                        this.value = event.target.value;
                    }}
                    ?disabled=${this.disabled}
                    ?readonly=${this.readonly}
                    ?required=${this.required}
                    placeholder=${ifDefined(this.placeholder)}
                    minlength=${ifDefined(this.minlength)}
                    maxlength=${ifDefined(this.maxlength)}
                    min=${ifDefined(this.min)}
                    max=${ifDefined(this.max)} />`,
            input => html`
                <div class="number-wrapper">
                    ${ input }
                    <div @click=${ () =>  { this.doStep('down') } } style="background-color: rgb(var(--sp-color-a))" class="number-minus"></div>
                    <div @click=${ () =>  { this.doStep('up') } } style="background-color: rgb(var(--sp-color-a))" class="number-plus"></div>
                </div>`
        );
    }

    private conditionallyWrap(condition: boolean, inner: TemplateResult, wrapFn: (template: TemplateResult) => TemplateResult) {
        return condition ? wrapFn(inner) : inner;
    }

    private doStep(direction: 'up' |'down') {
        const min = isNaN(+this.min) ? NaN : +this.min;
        const max = isNaN(+this.max) ? NaN : +this.max;
        const val = this.value ? +this.value : 0;
        const step = this.step === 'any' ? 1 : +this.step;
        if (
            (direction === 'down' && (isNaN(min) || val - step >= min))
        ) {
            if (!this.value && !isNaN(max) && val - step > max)
                this.value = max;
            else this.value = (val - step).toString();
        }
        if (direction === 'up' && (isNaN(max) || val + step <= max)) {
            if (!this.value && !isNaN(min) && val + step < min)
                this.value = min;
            else this.value = (val + step).toString();
        }
        this.emit('input');
    }

}
